import React, {useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {observer} from 'mobx-react';
import {useStores} from '../../../stores/Context';
import styles from './IndicatorInsights.module.scss';
import ManualIndicatorEntry from './ManualIndicatorEntry';
import {causesLabel, formatRecordedAt, observationAriaLabel} from './indicatorHistoryContext';
import {buildIndicatorChartSeries, shouldRenderChartLabel} from './indicatorChartSeries';
import {
    automaticCauseDateOptions,
    automaticCausePayload,
    causeLabelsForAutomaticDate,
} from './automaticIndicatorCauses';

const PERIODS = [7, 30, 90];

const number = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return Number(value).toLocaleString('fr-FR', {maximumFractionDigits: 2});
};

const dateLabel = (date, period) => {
    if (!date) return '';
    const options = period > 30 ? {day: '2-digit', month: 'short'} : {weekday: 'short'};
    return new Intl.DateTimeFormat('fr-FR', options).format(new Date(`${date}T12:00:00`));
};

const TrendIcon = ({trend}) => {
    const rotation = trend === 'up' ? -45 : trend === 'down' ? 45 : 0;
    return <span className={styles.trendIcon} style={{transform: `rotate(${rotation}deg)`}}>↗</span>;
};

const CATEGORY_COLORS = {
    matiere: '#00B0FF',
    methode: '#6BBD6A',
    milieu: '#FFB21A',
    machine: '#8A6CFF',
    main_oeuvre: '#FF7A59',
    management: '#008DCA',
};

const CausesPanel = ({causes}) => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const pareto = causes?.pareto || [];
    const items = causes?.items || [];
    const visibleItems = selectedCategory
        ? items.filter(cause => cause.category === selectedCategory)
        : items;

    if (!items.length) {
        return <div className={styles.paretoEmpty}>
            <span className={styles.paretoIcon}>⌁</span>
            <strong>Pas encore de causes qualifiées</strong>
            <p>Ajoutez une cause terrain sur une date ou reliez une cause de Résolution de problèmes à cet indicateur pour alimenter le Pareto et le contexte des actions Planning.</p>
        </div>;
    }

    return <div className={styles.causesPanel}>
        <p className={styles.causesLead}>Les causes qualifiées sur les relevés, les mesures automatiques et la Résolution de problèmes sont agrégées ici. Touchez une barre pour filtrer le détail.</p>
        <div className={styles.paretoBars} aria-label="Pareto des causes liées">
            {pareto.map(entry => <button
                key={entry.category}
                className={`${styles.paretoBar} ${selectedCategory === entry.category ? styles.paretoBarActive : ''}`}
                onClick={() => setSelectedCategory(selectedCategory === entry.category ? null : entry.category)}
                style={{'--pareto-color': CATEGORY_COLORS[entry.category] || '#00B0FF', '--pareto-share': `${Math.max(entry.share, 8)}%`}}
            >
                <span>{entry.label}</span><i><b/>{entry.share}%</i><small>{entry.count} {['manual', 'automatic'].includes(entry.source) ? 'jour' : 'cause'}{entry.count > 1 ? 's' : ''}</small>
            </button>)}
        </div>
        <div className={styles.linkedCauseList}>
            {visibleItems.map(cause => <article key={cause.id} className={styles.linkedCause}>
                <span className={styles.linkedCauseCategory}>{(pareto.find(entry => entry.category === cause.category)?.label) || cause.category}</span>
                <strong>{cause.description}</strong>
                {cause.source === 'manual' && <small>Relevé terrain · {cause.occurrences} jour{cause.occurrences > 1 ? 's' : ''} qualifié{cause.occurrences > 1 ? 's' : ''}</small>}
                {cause.source === 'automatic' && <small>Mesure automatique · {formatRecordedAt(cause.linked_at)}</small>}
                {cause.problem?.title && <small>RSP · {cause.problem.title}</small>}
            </article>)}
        </div>
    </div>;
};

const HistoryChart = ({history, period, unit, threshold}) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const view = {width: 720, height: 230, left: 44, right: 22, top: 22, bottom: 42};
    const series = buildIndicatorChartSeries(history);
    const chartHistory = series.points;
    const values = chartHistory.map(point => Number(point.value));
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 1;
    const extra = Math.max((max - min) * 0.15, 1);
    const lower = min - extra;
    const upper = max + extra;
    const range = Math.max(upper - lower, 1);
    const chartWidth = view.width - view.left - view.right;
    const chartHeight = view.height - view.top - view.bottom;
    const point = (entry, index) => ({
        x: view.left + (chartHistory.length <= 1 ? chartWidth / 2 : (index / (chartHistory.length - 1)) * chartWidth),
        y: view.top + chartHeight - ((Number(entry.value) - lower) / range) * chartHeight,
    });
    const points = chartHistory.map(point);
    const path = points.map((entry, index) => `${index === 0 ? 'M' : 'L'} ${entry.x} ${entry.y}`).join(' ');
    const thresholdY = threshold?.value !== undefined
        ? view.top + chartHeight - ((Number(threshold.value) - lower) / range) * chartHeight
        : null;
    const ticks = [0, .5, 1].map(ratio => lower + (upper - lower) * ratio);
    const activeObservation = activeIndex === null ? null : chartHistory[activeIndex];
    const activePoint = activeIndex === null ? null : points[activeIndex];
    const tooltipCause = activeObservation ? causesLabel(activeObservation.causes) : '';
    const tooltipX = activePoint ? Math.min(Math.max(activePoint.x - 108, view.left), view.width - view.right - 216) : 0;
    const tooltipY = activePoint ? Math.max(view.top, activePoint.y - 66) : 0;

    if (!history.length) {
        return <div className={styles.emptyChart}>
            <strong>Historique indisponible</strong>
            <span>Cette source ne fournit pas encore de mesures datées exploitables pour tracer son évolution.</span>
        </div>;
    }

    const observationSummary = (entry) => entry?.observation_count > 1
        ? `Moyenne de ${entry.observation_count} relevés · ${formatRecordedAt(entry.period_start)} → ${formatRecordedAt(entry.period_end)}`
        : formatRecordedAt(entry?.recorded_at);
    const chartAriaLabel = (entry) => entry?.observation_count > 1
        ? `Moyenne de ${entry.observation_count} relevés : ${number(entry.value)} ${unit}. Du ${formatRecordedAt(entry.period_start)} au ${formatRecordedAt(entry.period_end)}. ${causesLabel(entry.causes || [])}`
        : observationAriaLabel(entry, unit);

    return <>
        {series.aggregated && <div className={styles.chartAggregationNotice} role="status">
            <span>Lecture simplifiée</span><strong>{series.rawCount} relevés regroupés en moyennes temporelles</strong>
        </div>}
        <div className={styles.chartScroll}>
        <svg className={styles.chart} viewBox={`0 0 ${view.width} ${view.height}`} role="img" aria-label="Historique de l’indicateur">
            <defs>
                <linearGradient id="indicatorArea" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#00B0FF" stopOpacity=".28"/>
                    <stop offset="100%" stopColor="#00B0FF" stopOpacity="0"/>
                </linearGradient>
            </defs>
            {ticks.map((tick, index) => {
                const y = view.top + chartHeight - ((tick - lower) / range) * chartHeight;
                return <g key={index}>
                    <line x1={view.left} x2={view.width - view.right} y1={y} y2={y} className={styles.gridLine}/>
                    <text x={view.left - 12} y={y + 5} textAnchor="end" className={styles.axisText}>{number(tick)}</text>
                </g>;
            })}
            {thresholdY !== null && thresholdY >= view.top && thresholdY <= view.top + chartHeight && <g>
                <line x1={view.left} x2={view.width - view.right} y1={thresholdY} y2={thresholdY} stroke={threshold.color} strokeWidth="2" strokeDasharray="6 6"/>
                <text x={view.width - view.right} y={thresholdY - 8} textAnchor="end" fill={threshold.color} className={styles.thresholdText}>Seuil</text>
            </g>}
            {points.length > 1 && <path d={`${path} L ${points.at(-1).x} ${view.top + chartHeight} L ${points[0].x} ${view.top + chartHeight} Z`} fill="url(#indicatorArea)"/>}
            <path d={path} fill="none" stroke="#00B0FF" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
            {points.map((entry, index) => <g key={entry.id || `${entry.date}-${entry.recorded_at || index}`}>
                <circle
                    cx={entry.x}
                    cy={entry.y}
                    r={activeIndex === index ? "8" : series.aggregated ? "3" : "7"}
                    fill="#FFF"
                    stroke="#00B0FF"
                    strokeWidth={series.aggregated ? "2" : "4"}
                    className={styles.chartPoint}
                    tabIndex="0"
                    role="img"
                    aria-label={chartAriaLabel(entry)}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                    onFocus={() => setActiveIndex(index)}
                    onBlur={() => setActiveIndex(null)}
                ><title>{`${observationSummary(entry)} · ${causesLabel(entry.causes)}`}</title></circle>
                {shouldRenderChartLabel(index, points.length) && <text x={entry.x} y={view.height - 14} textAnchor="middle" className={styles.axisText}>{dateLabel(entry.date, period)}</text>}
            </g>)}
            {activeObservation && activePoint && <g className={styles.chartTooltip} pointerEvents="none">
                <rect x={tooltipX} y={tooltipY} width="216" height="54" rx="9"/>
                <text x={tooltipX + 10} y={tooltipY + 18}>{activeObservation.observation_count > 1 ? `${activeObservation.observation_count} relevés · moyenne` : formatRecordedAt(activeObservation.recorded_at)}</text>
                <text x={tooltipX + 10} y={tooltipY + 38}>{tooltipCause.length > 37 ? `${tooltipCause.slice(0, 37)}…` : tooltipCause}</text>
            </g>}
            <text x={view.width - view.right} y={20} textAnchor="end" className={styles.unitText}>{unit || ''}</text>
        </svg>
        </div>
    </>;
};

const ActionModal = ({insight, onClose, onCreate, isSubmitting}) => {
    const [title, setTitle] = useState(`Analyser ${insight.widget.name}`);
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState(2);
    const [error, setError] = useState('');

    const submit = async (event) => {
        event.preventDefault();
        if (!title.trim()) {
            setError('L’intitulé de l’action est requis.');
            return;
        }
        setError('');
        try {
            await onCreate({title: title.trim(), description: description.trim(), due_date: dueDate || null, priority});
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'La création de l’action a échoué.');
        }
    };

    return <div className={styles.modalOverlay} role="presentation" onMouseDown={onClose}>
        <form className={styles.actionModal} onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
            <div className={styles.modalHeader}>
                <div>
                    <span className={styles.eyebrow}>DU SIGNAL À L’ACTION</span>
                    <h3>Créer une action Planning</h3>
                </div>
                <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fermer">×</button>
            </div>
            <p className={styles.modalContext}>L’action sera créée dans le Planning d’Actions du Totem, avec la valeur et la tendance observées.</p>
            <label>Intitulé de l’action<input value={title} onChange={event => setTitle(event.target.value)} maxLength="191" autoFocus/></label>
            <label>Échéance (facultative)<input type="date" value={dueDate} onChange={event => setDueDate(event.target.value)}/></label>
            <label>Priorité<select value={priority} onChange={event => setPriority(Number(event.target.value))}>
                <option value={1}>Basse</option><option value={2}>Normale</option><option value={3}>Haute</option><option value={4}>Critique</option>
            </select></label>
            <label>Contexte / décision<textarea value={description} onChange={event => setDescription(event.target.value)} rows="4" maxLength="2000" placeholder="Précisez l’action attendue…"/></label>
            {error && <p className={styles.formError}>{error}</p>}
            <div className={styles.modalActions}>
                <button type="button" className={styles.secondaryButton} onClick={onClose}>Annuler</button>
                <button type="submit" className={styles.primaryButton} disabled={isSubmitting}>{isSubmitting ? 'Création…' : 'Créer dans le Planning →'}</button>
            </div>
        </form>
    </div>;
};

const ManualEntryModal = ({idWidget, unit, onClose, onPersisted}) => <div
    className={styles.modalOverlay}
    role="presentation"
    onMouseDown={onClose}
>
    <div
        className={`${styles.manualEntryModal} ${styles.actionModal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-entry-modal-title"
        onMouseDown={event => event.stopPropagation()}
    >
        <div className={styles.modalHeader}>
            <div>
                <span className={styles.eyebrow}>RELEVÉ TERRAIN</span>
                <h3 id="manual-entry-modal-title">Ajouter une saisie</h3>
            </div>
            <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <p className={styles.modalContext}>Ajoutez une observation, son heure de saisie et, si utile, les causes associées. Chaque saisie reste visible dans l’historique.</p>
        <ManualIndicatorEntry idWidget={idWidget} unit={unit} onPersisted={onPersisted} compact/>
    </div>
</div>;

const AutomaticCauseModal = ({history, unit, onClose, onPersisted, isSubmitting}) => {
    const dates = useMemo(() => automaticCauseDateOptions(history), [history]);
    const [observedOn, setObservedOn] = useState(dates[0]?.date || '');
    const [labels, setLabels] = useState(() => causeLabelsForAutomaticDate(history, dates[0]?.date));
    const [newLabel, setNewLabel] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!observedOn && dates[0]?.date) setObservedOn(dates[0].date);
    }, [dates, observedOn]);

    useEffect(() => {
        setLabels(causeLabelsForAutomaticDate(history, observedOn));
        setNewLabel('');
        setError('');
    }, [history, observedOn]);

    const addLabel = () => {
        const label = newLabel.trim();
        if (!label) return;
        if (labels.some(item => item.toLocaleLowerCase('fr-FR') === label.toLocaleLowerCase('fr-FR'))) {
            setError('Cette cause est déjà associée à cette date.');
            return;
        }
        setLabels(current => [...current, label]);
        setNewLabel('');
        setError('');
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!observedOn) {
            setError('Sélectionnez une date de mesure.');
            return;
        }
        setError('');
        try {
            await onPersisted(automaticCausePayload(observedOn, labels));
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Les causes n’ont pas été enregistrées.');
        }
    };

    const selectedObservation = dates.find(entry => entry.date === observedOn);
    const selectedDateLabel = observedOn
        ? new Intl.DateTimeFormat('fr-FR', {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'}).format(new Date(`${observedOn}T12:00:00`))
        : '';

    return <div className={styles.modalOverlay} role="presentation" onMouseDown={onClose}>
        <form className={`${styles.actionModal} ${styles.automaticCauseModal}`} onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
            <div className={styles.modalHeader}>
                <div>
                    <span className={styles.eyebrow}>QUALIFICATION TERRAIN</span>
                    <h3>Associer des causes à une mesure auto</h3>
                </div>
                <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fermer">×</button>
            </div>
            <p className={styles.modalContext}>La valeur reste fournie par YDOS. Vous ajoutez uniquement le contexte opérationnel, visible dans l’historique et le Pareto.</p>
            <label>Date de la mesure
                <select value={observedOn} onChange={event => setObservedOn(event.target.value)}>
                    {dates.map(entry => <option key={entry.date} value={entry.date}>
                        {new Intl.DateTimeFormat('fr-FR', {day: '2-digit', month: 'short', year: 'numeric'}).format(new Date(`${entry.date}T12:00:00`))} · {number(entry.value)} {unit || ''}
                    </option>)}
                </select>
            </label>
            {selectedDateLabel && <div className={styles.automaticCauseDate}><strong>{selectedDateLabel}</strong>{selectedObservation && <span>Valeur mesurée : {number(selectedObservation.value)} {unit || ''}</span>}</div>}
            <div className={styles.automaticCauseEditor}>
                <span>Causes de cette date</span>
                <div className={styles.automaticCauseAdd}>
                    <input value={newLabel} maxLength="160" placeholder="Ex. Attente matière" onChange={event => setNewLabel(event.target.value)} onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            addLabel();
                        }
                    }}/>
                    <button type="button" onClick={addLabel}>Ajouter</button>
                </div>
                {labels.length > 0 ? <div className={styles.automaticCauseChips}>
                    {labels.map(label => <span key={label}>{label}<button type="button" aria-label={`Retirer ${label}`} onClick={() => setLabels(current => current.filter(item => item !== label))}>×</button></span>)}
                </div> : <p className={styles.automaticCauseEmpty}>Aucune cause pour cette date. Enregistrez sans cause pour retirer une qualification existante.</p>}
            </div>
            {error && <p className={styles.formError}>{error}</p>}
            <div className={styles.modalActions}>
                <button type="button" className={styles.secondaryButton} onClick={onClose}>Annuler</button>
                <button type="submit" className={styles.primaryButton} disabled={isSubmitting}>{isSubmitting ? 'Enregistrement…' : 'Enregistrer les causes'}</button>
            </div>
        </form>
    </div>;
};

const IndicatorInsights = observer(({idWidget}) => {
    const {widgetStore, uiStore, PlanningActionsStore} = useStores();
    const [period, setPeriod] = useState(7);
    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showActionModal, setShowActionModal] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [showAutomaticCauseModal, setShowAutomaticCauseModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [savingAutomaticCauses, setSavingAutomaticCauses] = useState(false);
    const [createdTask, setCreatedTask] = useState(null);

    const headers = useMemo(() => ({Authorization: `Bearer ${widgetStore._token}`}), [widgetStore._token]);

    const enrichWithLiveYdosValue = async (payload) => {
        if (payload?.widget?.source !== 'ydos') return payload;
        if ((payload?.insights?.history || []).length > 0) return payload;

        const configuration = widgetStore.widgetsAvailable.find(widget => String(widget.idWidget) === String(idWidget))?.config;
        const queryId = configuration?.ind_id_requete_ydos;
        const valueField = configuration?.ind_type_valeur;
        if (!queryId || !valueField) return payload;

        try {
            const liveResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/requete/${queryId}`, {headers});
            const rawValue = liveResponse.data?.[0]?.[valueField];
            if (rawValue === undefined || rawValue === null || rawValue === '') return payload;

            const value = Number(String(rawValue).replace(',', '.'));
            if (!Number.isFinite(value)) return payload;

            return {
                ...payload,
                insights: {
                    ...payload.insights,
                    current: {date: new Date().toISOString().slice(0, 10), value},
                },
            };
        } catch (_) {
            return payload;
        }
    };

    const load = async () => {
        if (!widgetStore._token || !idWidget) return;
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/indicateurs/${idWidget}/insights`, {headers, params: {period}});
            setInsight(await enrichWithLiveYdosValue(response.data?.data || null));
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Les données de l’indicateur sont indisponibles.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [idWidget, period, widgetStore._token]);

    const createAction = async (payload) => {
        setSubmitting(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/indicateurs/${idWidget}/planning-actions`,
                {...payload, snapshot: insight.insights},
                {headers},
            );
            setCreatedTask(response.data?.data?.task || null);
            setShowActionModal(false);
            await load();
        } finally {
            setSubmitting(false);
        }
    };

    const saveAutomaticCauses = async (payload) => {
        setSavingAutomaticCauses(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/indicateurs/${idWidget}/automatic-causes/${payload.observed_on}`,
                payload,
                {headers},
            );
            await load();
            setShowAutomaticCauseModal(false);
        } finally {
            setSavingAutomaticCauses(false);
        }
    };

    const openPlanning = (taskToOpen = createdTask) => {
        const planning = insight?.planning;
        if (!planning) return;
        const planningWidget = widgetStore.widgetsAvailable.find(widget =>
            widget.typeWidget === 'WidgetPlanningActions' && String(widget.configWidget) === String(planning.id)
        );
        if (!planningWidget) {
            setError('Le phidget Planning associé est introuvable sur ce Totem.');
            return;
        }
        uiStore.openConsultingModePlan2do(
            PlanningActionsStore,
            widgetStore.getCookie('current_apiToken'),
            planningWidget.idWidget,
            planning.id,
            'Kanban',
        );
        if (taskToOpen?.id) {
            window.setTimeout(() => PlanningActionsStore.openModalFormAction(null, null, null, taskToOpen.id), 400);
        }
    };

    if (loading) return <div className={styles.loading}>Chargement de l’analyse opérationnelle…</div>;
    if (error && !insight) return <div className={styles.errorState}><strong>Analyse indisponible</strong><span>{error}</span><button onClick={load}>Réessayer</button></div>;
    if (!insight) return null;

    const {widget, insights, planning, actions} = insight;
    const current = insights.current?.value;
    const thresholdColor = insights.threshold?.color || '#00B0FF';

    return <section className={styles.workspace}>
        <header className={styles.header}>
            <div>
                <span className={styles.eyebrow}>INDICATEUR · PILOTAGE</span>
                <h2>{widget.name}</h2>
                <p>{widget.totem_name} · Lecture opérationnelle et passage à l’action</p>
            </div>
        </header>

        {error && <div className={styles.inlineError}>{error}</div>}
        <div className={styles.summaryGrid}>
            <article className={`${styles.summaryCard} ${styles.currentCard}`}>
                <span>Valeur actuelle</span>
                <strong>{number(current)} <small>{widget.unit}</small></strong>
                <em>{insights.current?.date ? `Dernier relevé le ${new Intl.DateTimeFormat('fr-FR').format(new Date(`${insights.current.date}T12:00:00`))}` : 'Aucun relevé disponible'}</em>
            </article>
            <article className={styles.summaryCard}>
                <span>Évolution</span>
                <strong className={styles.trendValue}><TrendIcon trend={insights.trend}/> {insights.delta === null ? '—' : `${insights.delta > 0 ? '+' : ''}${number(insights.delta)}`}</strong>
                <em>{insights.delta_percent === null ? 'Deux relevés sont nécessaires' : `${insights.delta_percent > 0 ? '+' : ''}${insights.delta_percent}% vs relevé précédent`}</em>
            </article>
            <article className={styles.summaryCard} style={{'--status-color': thresholdColor}}>
                <span>Seuil opérationnel</span>
                <strong>{insights.threshold?.label || 'Non configuré'}</strong>
                <em>{insights.threshold ? 'Évaluation sur la dernière valeur' : 'Configurez un seuil pour une alerte lisible'}</em>
            </article>
            <article className={styles.summaryCard}>
                <span>Observations</span>
                <strong>{insights.statistics?.observations ?? '—'}</strong>
                <em>{insights.statistics ? `Moyenne ${number(insights.statistics.average)} ${widget.unit || ''}` : 'Aucune série analysable'}</em>
            </article>
        </div>

        <section className={styles.panel}>
            <div className={styles.panelHeader}>
                <div><span className={styles.panelKicker}>HISTORIQUE RÉEL</span><h3>Évolution de la mesure</h3></div>
                <div className={styles.historyActions}>
                    <div className={styles.periodSwitch} aria-label="Période analysée">
                        {PERIODS.map(value => <button type="button" key={value} className={period === value ? styles.activePeriod : ''} onClick={() => setPeriod(value)}>{value} j</button>)}
                    </div>
                    {widget.source === 'manuel' && <button type="button" className={styles.manualEntryButton} onClick={() => setShowManualEntry(true)}>＋ Ajouter une saisie</button>}
                    {widget.source === 'ydos' && (insights.history || []).length > 0 && <button type="button" className={styles.automaticCauseButton} onClick={() => setShowAutomaticCauseModal(true)}>＋ Qualifier une date</button>}
                </div>
            </div>
            <HistoryChart history={insights.history || []} period={period} unit={widget.unit} threshold={insights.threshold}/>
            {insights.message && <p className={styles.dataNotice}>{insights.message}</p>}
        </section>

        <div className={styles.lowerGrid}>
            <section className={styles.panel}>
                <div className={styles.panelHeader}><div><span className={styles.panelKicker}>QUALITÉ DE DONNÉES</span><h3>Causes et leviers</h3></div></div>
                <CausesPanel causes={insight.causes}/>
            </section>
            <section className={`${styles.panel} ${styles.actionPanel}`}>
                <div className={styles.panelHeader}><div><span className={styles.panelKicker}>PASSAGE À L’ACTION</span><h3>Planning d’Actions</h3></div></div>
                {planning ? <>
                    <p className={styles.planningLead}>Créez une tâche contextualisée dans <strong>{planning.name}</strong>. La valeur, la tendance et le seuil restent traçables depuis l’indicateur.</p>
                    <button className={styles.createActionButton} onClick={() => setShowActionModal(true)}>＋ Créer une action dans le Planning</button>
                    {createdTask && <div className={styles.actionFeedback} role="status">
                        <span>✓ Action créée</span>
                        <strong>{createdTask.title || 'Nouvelle action Planning'}</strong>
                        <button className={styles.openPlanningButton} onClick={openPlanning}>Ouvrir l’action dans le Planning →</button>
                    </div>}
                </> : <div className={styles.planningMissing}><strong>Planning non configuré</strong><span>Associez d’abord un Planning d’Actions à ce Totem pour créer des actions depuis l’indicateur.</span></div>}
                {(actions || []).length > 0 && <div className={styles.actionList}>
                    <span className={styles.actionListTitle}>Dernières actions créées</span>
                    {actions.map(action => <button key={action.id} className={styles.actionRow} onClick={() => { setCreatedTask(action.task); openPlanning(action.task); }}>
                        <span>{action.task?.title || 'Action archivée'}</span><small>{action.task?.status || 'À suivre'}</small>
                    </button>)}
                </div>}
            </section>
        </div>

        {showActionModal && <ActionModal insight={insight} isSubmitting={submitting} onClose={() => setShowActionModal(false)} onCreate={createAction}/>} 
        {showManualEntry && <ManualEntryModal
            idWidget={idWidget}
            unit={widget.unit}
            onClose={() => setShowManualEntry(false)}
            onPersisted={async () => {
                await load();
                setShowManualEntry(false);
            }}
        />}
        {showAutomaticCauseModal && <AutomaticCauseModal
            history={insights.history || []}
            unit={widget.unit}
            isSubmitting={savingAutomaticCauses}
            onClose={() => setShowAutomaticCauseModal(false)}
            onPersisted={saveAutomaticCauses}
        />}
    </section>;
});

export default IndicatorInsights;
